import { useTranslation } from 'react-i18next';

import {
  KEYBOARD_HIGH_MIDI,
  KEYBOARD_LOW_MIDI,
  KEYBOARD_TICKS,
  keyboardRatio,
} from './chartGeometry';

/**
 * 건반형 음역 막대 (시안 §8.4 `994:6678`) — 반음 하나가 칸 하나. 낸 음역은 레드, 못 낸
 * 음은 회색. 양 끝 위에 음이름 알약이 서고, 아래에 `C2 … C6` 눈금과 범례가 있다.
 *
 * 창은 측정 화면의 세로 게이지(`PitchGaugeTrack`)와 **같다** — 두 화면이 같은 목소리를
 * 다른 폭으로 그리면 진단 중에 본 위치와 결과에서 본 위치가 어긋난다.
 */

const CELLS = KEYBOARD_HIGH_MIDI - KEYBOARD_LOW_MIDI + 1;

export const KeyboardRangeBar = ({
  lowMidi,
  highMidi,
  lowLabel,
  highLabel,
  legend,
}: {
  lowMidi: number;
  highMidi: number;
  lowLabel: string;
  highLabel: string;
  /** 칠한 곳과 안 칠한 곳이 각각 무엇인지. 시안은 이 두 줄을 막대 아래 둔다. */
  legend: { filled: string; empty: string };
}) => {
  const { t } = useTranslation();
  const low = Math.min(lowMidi, highMidi);
  const high = Math.max(lowMidi, highMidi);
  const lowX = keyboardRatio(low) * 100;
  const highX = keyboardRatio(high) * 100;

  return (
    <div className="mt-3">
      {/* 양 끝 음이름 알약 — 막대 위, 그 음의 자리에. 서로 가까우면 겹치므로 각각
          자기 끝 쪽으로 붙인다(왼쪽 알약은 오른쪽 정렬, 오른쪽 알약은 왼쪽 정렬). */}
      <div className="relative h-6">
        <span
          className="absolute -translate-x-1/2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-white"
          style={{ left: `clamp(16px, ${lowX}%, calc(100% - 16px))` }}
        >
          {lowLabel}
        </span>
        <span
          className="absolute -translate-x-1/2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-white"
          style={{ left: `clamp(16px, ${highX}%, calc(100% - 16px))` }}
        >
          {highLabel}
        </span>
      </div>

      <div
        role="img"
        aria-label={t('web.result.range.notes', {
          low: lowLabel,
          high: highLabel,
        })}
        className="mt-1.5 flex h-6 w-full gap-px"
      >
        {Array.from({ length: CELLS }, (_, i) => {
          const midi = KEYBOARD_LOW_MIDI + i;
          const inRange = midi >= low && midi <= high;
          return (
            <span
              key={midi}
              data-testid={inRange ? 'range-cell-on' : 'range-cell-off'}
              className={
                inRange
                  ? 'flex-1 rounded-[2px] bg-primary'
                  : 'flex-1 rounded-[2px] bg-bk-200'
              }
            />
          );
        })}
      </div>

      <div className="relative mt-1.5 h-4">
        {KEYBOARD_TICKS.map((tick) => (
          <span
            key={tick.label}
            aria-hidden="true"
            className="absolute -translate-x-1/2 text-[11px] tabular-nums text-bk-600"
            style={{ left: `clamp(8px, ${tick.x}%, calc(100% - 10px))` }}
          >
            {tick.label}
          </span>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-4 text-xs text-bk-800">
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-2 w-2 rounded-full bg-primary"
          />
          {legend.filled}
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-bk-200" />
          {legend.empty}
        </span>
      </div>
    </div>
  );
};
